import React from 'react';
import {
  FaHeart,
  FaUsers,
  FaGlobeAmericas,
  FaAward,
  FaRocket,
  FaHandshake
} from 'react-icons/fa';

const About: React.FC = () => {
  const values = [
    {
      icon: <FaAward className="text-3xl" />,
      title: 'Excellence in Engineering',
      description: 'We take pride in writing clean, maintainable code that solves real business problems. Quality isn\'t negotiable.'
    },
    {
      icon: <FaHandshake className="text-3xl" />,
      title: 'Long-Term Partnership',
      description: 'We\'re not here for quick projects and disappearing acts. We build relationships that last years, not months.'
    },
    {
      icon: <FaUsers className="text-3xl" />,
      title: 'Transparent Communication',
      description: 'No jargon, no surprises. We speak plainly about progress, challenges, and timelines.'
    },
    {
      icon: <FaRocket className="text-3xl" />,
      title: 'Business-First Mindset',
      description: 'Technology serves the business, not the other way around. We optimize for your success, not our tech preferences.'
    }
  ];

  const stats = [
    { number: '10+', label: 'Years Average Experience', icon: '📚' },
    { number: '30+', label: 'Senior Engineers', icon: '👨‍💻' },
    { number: '3', label: 'Continents Served', icon: '🌍' },
    { number: '24/7', label: 'Support Coverage', icon: '⏰' }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            About Us
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We are a team of experienced engineers focused on building reliable software systems
            that support real business growth. No hype, no buzzwords — just professional software development.
          </p>
        </div>

        {/* Mission Statement */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 mb-16 text-white">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <div className="bg-white/20 p-6 rounded-full">
                <FaHeart className="text-6xl text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-3xl font-bold mb-4">Our Mission</h3>
              <p className="text-xl text-blue-100 leading-relaxed">
                To be the most reliable engineering partner for businesses that need to build, scale, and maintain
                production-grade software. We measure our success by the long-term success of our clients.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="text-4xl mb-3">{stat.icon}</div>
              <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
              <div className="text-gray-700 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Our Values */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Our Values
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-4 rounded-lg text-blue-600 flex-shrink-0">
                    {value.icon}
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      {value.title}
                    </h4>
                    <p className="text-gray-600">
                      {value.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Engineering Culture */}
        <div className="bg-white rounded-2xl shadow-lg p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Our Engineering Culture
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                  ✓
                </span>
                Senior-Level Focus
              </h4>
              <p className="text-gray-600 mb-6">
                We hire senior engineers with 10+ years of experience who have built and scaled production systems.
                No junior developers, no learning on your dime.
              </p>

              <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                  ✓
                </span>
                Continuous Learning
              </h4>
              <p className="text-gray-600 mb-6">
                Our engineers invest in staying current with technology trends while maintaining deep expertise
                in proven frameworks and patterns.
              </p>

              <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                  ✓
                </span>
                Remote-First & Distributed
              </h4>
              <p className="text-gray-600">
                We've built a distributed team that excels at remote collaboration, with strong communication
                practices and async-friendly workflows.
              </p>
            </div>

            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                  ✓
                </span>
                Cross-Functional Teams
              </h4>
              <p className="text-gray-600 mb-6">
                Our teams include backend, frontend, DevOps, and QA engineers working together — not siloed specialists.
              </p>

              <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                  ✓
                </span>
                Quality-Driven Development
              </h4>
              <p className="text-gray-600 mb-6">
                We practice test-driven development, continuous integration, and code reviews as standard operating procedure.
              </p>

              <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                  ✓
                </span>
                English-Fluent Communication
              </h4>
              <p className="text-gray-600">
                All our engineers are fluent in English with strong written and verbal communication skills — critical for remote collaboration.
              </p>
            </div>
          </div>
        </div>

        {/* Geographic Coverage */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-gray-50 to-white rounded-2xl p-12 border border-gray-200">
            <FaGlobeAmericas className="text-6xl text-blue-600 mx-auto mb-6" />
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              International Coverage
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              We work with companies across the United States, Canada, and European Union. Our distributed team
              ensures time-zone overlap with your business hours and English-fluent communication.
            </p>
            {/* <div className="flex flex-wrap justify-center gap-6">
              <div className="bg-white rounded-lg shadow-md px-6 py-3 border border-gray-200">
                <span className="font-semibold text-gray-900">🇺🇸 United States</span>
              </div>
              <div className="bg-white rounded-lg shadow-md px-6 py-3 border border-gray-200">
                <span className="font-semibold text-gray-900">🇨🇦 Canada</span>
              </div>
              <div className="bg-white rounded-lg shadow-md px-6 py-3 border border-gray-200">
                <span className="font-semibold text-gray-900">🇪🇺 European Union</span>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
